/**
 * SignalDrawerContainer — connects the presentational SignalDrawer to the
 * `useCreateInvestigation` mutation (cycle-109 sprint-205 S3.4).
 *
 * Owns:
 *   - mutation lifecycle (`useCreateInvestigation`)
 *   - FR-6.3 payload assembly (via `buildCreateInvestigationPayload`)
 *   - 401 → JWT-required affordance (AC-3.4.c, SDD §7.2)
 *   - success → caller-supplied `onCreateSuccess(investigationId)` so the
 *     page can auto-open the InvestigationDrawer (sprint-plan §S3.4)
 *
 * Does NOT own:
 *   - cluster derivation (caller passes pre-derived cluster)
 *   - drawer mount/unmount (page-level orchestration in S3.6)
 */

import axios from 'axios';
import { useCreateInvestigation } from '../../hooks/useInvestigation';
import {
  JWT_AFFORDANCE_TEXT,
  SignalDrawer,
  buildCreateInvestigationPayload,
} from './SignalDrawer';
import type { WorldMonitorSignal } from '../../api/worldMonitor';

export interface SignalDrawerContainerProps {
  signal: WorldMonitorSignal;
  cluster?: readonly WorldMonitorSignal[];
  onClose: () => void;
  /** Fires after 201 success with the new investigation id. Caller routes
   * the page state to open the InvestigationDrawer on this id. */
  onCreateSuccess?: (investigationId: string) => void;
  className?: string;
}

export function SignalDrawerContainer({
  signal,
  cluster = [],
  onClose,
  onCreateSuccess,
  className,
}: SignalDrawerContainerProps) {
  const mutation = useCreateInvestigation();

  const actionError = mutationErrorAffordance(mutation.error);

  return (
    <SignalDrawer
      signal={signal}
      cluster={cluster}
      onClose={onClose}
      className={className}
      investigateInFlight={mutation.isPending}
      actionError={actionError}
      onInvestigate={(sig, clu) => {
        mutation.mutate(buildCreateInvestigationPayload(sig, clu), {
          onSuccess: (data) => {
            onCreateSuccess?.(data.id);
          },
        });
      }}
    />
  );
}

// ── Pure helper (exported for vitest) ──────────────────────────────

/**
 * Translate a mutation error into the inline affordance string. Only 401
 * gets the canonical JWT text (AC-3.4.c verbatim). Other errors surface a
 * generic message so the operator sees *something* but isn't misled into
 * thinking auth is the problem.
 */
export function mutationErrorAffordance(error: unknown): string | null {
  if (!error) return null;
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return JWT_AFFORDANCE_TEXT;
    const status = error.response?.status;
    if (status) return `Investigation create failed (${status}). Retry or check console for detail.`;
    return 'Investigation create failed — network or server unreachable.';
  }
  return 'Investigation create failed — see console for detail.';
}
